import {action, computed, makeObservable, observable} from "mobx";
import {DefaultEstate, EstateModel} from "../helpers/estate";
import {PropertyTypeId} from "../helpers/propertyType";
import {DefaultRequestStatus, RequestStatus} from "../services/services/requestStatus";
import {SimpleAddressModel} from "../helpers/address";
import {Package, PackageId, PackageModel} from "../helpers/package";
import {AdditionalInsurancesModel, DefaultAdditionalInsurances} from "../helpers/additionalInsurances";
import {AdditionalQuestionsModel, DefaultAdditionalQuestions} from "../helpers/additionalQuestions/additionalQuestions";
import {RisksId} from "../helpers/risk";
import {DefaultOcStandard, OcGuaranteeSum} from "../helpers/oc";
import {LegalProtectionVariant} from "../helpers/legalProtection";
import {CountId} from "../helpers/dropDown";
import {NumberOfDamagesInLastThreeYearsId} from "../helpers/additionalQuestions/numberOfDamagesInLastThreeYears";
import {ClaimlessYearsId} from "../helpers/additionalQuestions/claimlessYearsIn5Years";
import {PolicyDurationId} from "../helpers/additionalQuestions/policyDuration";
import {CalculationResponseModel, DefaultCalculationResponse} from "../helpers/calculation";
import {
  AdditionalProtectionsModel,
  DefaultAdditionalProtections
} from "../helpers/additionalQuestions/additionalProtections";
import { TypeOfActivityId } from "../helpers/additionalQuestions/typeOfActivity";
import {
  DefaultNnwClientsSubtractionCount,
  NnwClientsSubtractionCountModel
} from "../helpers/nnwClientsSubtractionCount";
import { PaymentModeId } from "../helpers/additionalQuestions/paymentMode";
import { BusinessActivityId, DefaultBusinessActivityInBuilding } from "../helpers/additionalQuestions/businessActivity";
import { DefaultPartOfFarm } from "../helpers/additionalQuestions/partOfFarm";
import { DefaultNnwType } from "../helpers/nnw";
import {
  Discounts,
  SelectedValuesDiscountModel,
  SelectedValuesDiscount,
  OtherClaimlessPolicyDiscountTypes
} from "../helpers/discount";
import { ApkSurvey, NewAnswerModel, OldAnswerModel } from "../helpers/ApkSurvey";
import { DirectAnswersId } from "../helpers/DirectAnswers";
import { InDirectAnswersId } from "../helpers/InDirectAnswers";
import { RisksNetworkTypes } from "../helpers/risksNetwork";
import {PolicyStore} from "./policyStore";

export interface CalculateStore {
  // APK
  apkUuid: string;
  apkSurvey: ApkSurvey;
  setApkUsingNewModel: (apkUuid: string, answers: NewAnswerModel[], isDirect: boolean) => void;
  setApkUsingOldModel: (answers: OldAnswerModel, isDirect: boolean) => void;
  setApkOnlyNnw: (isDirect: boolean) => void;
  setApkExternal: (isDirect: boolean) => void;
  setApkExternalNoClientConsentNo: () => void;
  getApkAnswers: string[];
  getAnswerOldModelValue: (answer: keyof typeof DirectAnswersId | keyof typeof InDirectAnswersId) => boolean | undefined;
  resetApk: () => void;
  externalApk: boolean;
  clientConsentForApk: boolean;
  externalApkNo: boolean;
  clientConsentForApkNo: boolean;
  propertyInsuranceApk: boolean;
  businessActivity: boolean;
  businessActivityInGarageOrFarmBuilding: boolean;
  availableBusinessActivity: boolean;
  businessActivityArea: boolean;
  farm: boolean;
  onlyHouse: boolean;
  oc: boolean;
  nnw: boolean;
  externalApkNoAndClientConsentNo: boolean;

  // Estate
  estate: EstateModel;
  setEstate: (estate: EstateModel) => void;
  resetFloor: () => void;
  resetEstate: () => void;
  isPropertyUnderConstruction: () => boolean;
  isFlat: boolean;

  // Packages
  packages: Map<PackageId, PackageModel>;
  setPackages: (packages: Map<PackageId, PackageModel>) => void;
  selectedPackage: PackageId;
  selectPackage: (packageId: PackageId) => void;
  resetSelectPackage: () => void;
  setPackageRisk: (packageId: PackageId, riskId: RisksId, checked: boolean) => void;
  setPackageInsuranceSum: (packageId: PackageId, riskId: RisksId, insuranceSum: number) => void;
  setErrorForPackageRisk: (packageId: PackageId, riskId: RisksId, hasError: boolean) => void;
  setErrorForPackageRiskInsuranceSum: (packageId: PackageId, riskId: RisksId, hasError: boolean) => void;
  setRequireRecalculatePackage: (packageId: PackageId, requireRecalculate: boolean) => void;
  setRequireRecalculatePackages: () => void;
  resetPackages: () => void;
  noRequiredInsuranceProductsSelectedForPackage: (packageId: PackageId) => boolean;

  // AdditionalInsurances
  additionalInsurances: AdditionalInsurancesModel;
  displaySecondOpinion: boolean;
  setAdditionalInsuranceFieldChecked: (additionalInsuranceField: RisksId, checked: boolean) => void;
  setInsuranceSumWithOptionalRecalculation: (riskId: RisksId, insuranceSum: number, shouldRecalculate: boolean) => void;
  setNonResidentialBuildingFlammable: (flammableStructure: boolean | undefined) => void;
  setDogsCount: (dogsCount: CountId) => void;
  setCatsCount: (catsCount: CountId) => void;
  setOcStandard: (ocGuaranteeSum: OcGuaranteeSum) => void;
  setOcAdditionalPropertyAddress: (fieldName: keyof SimpleAddressModel, newValue: string) => void;
  propertyInsurance: boolean;
  setPropertyInsurance: (propertyInsurance: boolean, fromApk?: boolean) => void;
  propertyInsuranceOrOcChecked: boolean;
  setNnwNumberOfClients: (nnwNumberOfClients: CountId) => void;
  setNnwNumberOfSeniors: (nnwNumberOfSeniors: CountId) => void;
  setNnwNumberOfHeartAttackAndStrokeInsureds: (numberOfClients: CountId) => void;
  setLegalProtectionVariant: (legalProtectionVariant: LegalProtectionVariant) => void;
  nnwClientsSubtractionCount: NnwClientsSubtractionCountModel;
  setSafeSeniorCount: (safeSeniorCount: CountId) => void;
  setAdditionalInsurancesRate: (riskId: RisksId, rate: number | null) => void;
  resetAdditionalInsurances: () => void;
  resetAdditionalInsurancesWithoutNnwAndOc: () => void;
  resetAdditionalInsurancesWithoutNnw: () => void;
  resetAdditionalInsurancesWithoutOc: () => void;
  resetCalculationResponse: () => void;
  setPropertyInsurancesOcNnw: () => void;
  refreshCalcultionRequire: () => void;

  // AdditionalQuestions
  additionalQuestions: AdditionalQuestionsModel;
  setAdditionalQuestions: (additionalQuestions: AdditionalQuestionsModel) => void;
  resetAdditionalQuestions: () => void;
  setApkToAdditionalQuestions: () => void;
  setBusinessActivityInBuildingValue: (businessActivity: BusinessActivityId | undefined) => void;
  setTypeOfActivity: (val: TypeOfActivityId) => void;
  setNumberOfDamagesInLastThreeYears: (numberOfDamagesInLastThreeYears: NumberOfDamagesInLastThreeYearsId | undefined) => void;
  setClaimlessYearsIn5Years: (claimlessYearsIn5Years?: ClaimlessYearsId) => void;
  setPolicyDuration: (policyDuration: PolicyDurationId) => void;
  setPartOfFarm: (partOfFarm: boolean | undefined) => void;
  setStartDate: (startDate: Date) => void;
  setPaymentMode: (paymentMode: PaymentModeId | "") => void;
  setAdditionalProtections: (additionalProtections: AdditionalProtectionsModel) => void;
  resetAdditionalProtections: () => void;
  resetPropertyAdditionalQuestion: () => void;

  // DiscountsAndAdditionalClauses
  discounts: SelectedValuesDiscountModel;
  additionalClauses: string;
  setDiscount: (name: Discounts, value: string) => void;
  setOtherClaimlessPolicyDiscount: (type: OtherClaimlessPolicyDiscountTypes, value: string) => void;
  setAdditionalClauses: (value: string) => void;
  resetDiscountsAndAdditionalClauses: () => void;
  setDiscountsAndAdditionalClauses: (discounts: SelectedValuesDiscount, additionalClauses: string) => void;
  setInitialDiscount: (discountType: Discounts, value: string) => void;

  // Requests
  calculationRequestStatus: RequestStatus;
  allCalculationRequestStatuses: RequestStatus[];
  allRiskNetworkRequestStatuses: RequestStatus[];
  allRequestStatuses: RequestStatus[];
  saveCalculationRequestStatus: RequestStatus;

  // Other
  requireRecalculate: boolean;
  setRequireRecalculate: (requireRecalculate: boolean) => void;

  calculationResponse: CalculationResponseModel | null;
  setCalculationResponse: (calculationResponse: CalculationResponseModel) => void;
  resetStore: () => void;

  parentCalculationId: string | undefined;
  setParentCalculationId: (parentCalculationId: string | undefined) => void;
  resetParentCalculationId: () => void;

  initialFloodState: boolean;
  setInitialFloodState: (floodState: boolean) => void;
  resetInitialFloodState: () => void;
}

export class DefaultCalculateStore implements CalculateStore {

  constructor(
      private policyStore?: PolicyStore
  ) {
    makeObservable(this);
  }

  // APK

  @observable
  private _apkUuid = "";

  @computed
  public get apkUuid() {
    return this._apkUuid;
  }

  @action
  public setApkUuid(apkUuid: string) {
    this._apkUuid = apkUuid;
  }

  @observable
  private _apkSurvey = new ApkSurvey([], true);

  @computed
  public get apkSurvey() {
    return this._apkSurvey;
  }

  @action
  public resetApk() {
    this._apkSurvey = new ApkSurvey([], false);
  }

  @action
  public setApkUsingNewModel(apkUuid: string, answers: NewAnswerModel[], isDirect: boolean) {
    this.setApkUuid(apkUuid);
    this._apkSurvey = new ApkSurvey(answers, isDirect);
  }

  @action
  public setApkUsingOldModel(answers: OldAnswerModel, isDirect: boolean) {
    this._apkSurvey = ApkSurvey.buildSurveyFromOldAnswerModel(answers, isDirect);
  }

  @action
  public setApkOnlyNnw(isDirect: boolean) {
    this._apkSurvey = ApkSurvey.buildSurveyOnlyNnw(isDirect);
  }

  @action
  public setApkExternal(isDirect: boolean) {
    this._apkSurvey = ApkSurvey.buildSurveyApkExternal(isDirect);
  }

  @action
  public setApkExternalNoClientConsentNo() {
    this._apkSurvey = ApkSurvey.buildSurveyApkExternalNoClientConsentNo();
  }

  @action
  public getAnswerOldModelValue(answer: keyof typeof DirectAnswersId | keyof typeof InDirectAnswersId) {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.getAnswerOldModelValue(answer as keyof typeof DirectAnswersId);
    } else if (!this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.getAnswerOldModelValue(answer as keyof typeof InDirectAnswersId);
    } else {
      // Handle the case where 'answer' is neither DirectAnswersId nor InDirectAnswersId
      throw new Error("Invalid answer ID");
    }
  }

  @computed
  public get getApkAnswers() {
    return this._apkSurvey.getAnswersUuids();
  }

  @computed
  public get externalApk() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.externalApk);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.externalApk);
    }
  }

  @computed
  public get clientConsentForApk() {
    return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.clientConsentForAPK);
  }

  @computed
  public get externalApkNo() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.externalApk_No);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.externalApk_No);
    }
  }

  @computed
  public get clientConsentForApkNo() {
    return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.clientConsentForAPK_No);
  }

  @computed
  public get propertyInsuranceApk() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.propertyInsurance);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.propertyInsurance);
    }
  }

  @computed
  public get businessActivity() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.businessActivity);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.businessActivity);
    }
  }

  @computed
  public get businessActivityInGarageOrFarmBuilding() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.businessActivityInGarageOrFarmBuilding);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.businessActivityInGarageOrFarmBuilding);
    }
  }

  @computed
  public get availableBusinessActivity() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.availableBusinessActivity);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.availableBusinessActivity);
    }
  }

  @computed
  public get businessActivityArea() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.businessActivityArea);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.businessActivityArea);
    }
  }

  @computed
  public get farm() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.farm);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.farm);
    }
  }

  @computed
  public get onlyHouse() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.onlyHouse);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.onlyHouse);
    }
  }

  @computed
  public get oc() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.oc);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.oc);
    }
  }

  @computed
  public get nnw() {
    if (this._apkSurvey.isDirectSurvey()) {
      return this._apkSurvey.isQuestionAnswered(DirectAnswersId.nnw);
    } else {
      return this._apkSurvey.isQuestionAnswered(InDirectAnswersId.nnw);
    }
  }

  // Estate
  @observable
  private _estate: EstateModel = new DefaultEstate();

  @computed
  public get estate() {
    return this._estate;
  }

  @action
  public setEstate(estate: EstateModel) {
    this._estate = estate;
  }

  @action
  public resetEstate() {
    this._estate = new DefaultEstate();
  }

  @action resetFloor() {
    this._estate.floor = null;
  }

  @action
  public isPropertyUnderConstruction() {
    switch (this._estate.propertyType) {
      case PropertyTypeId.detachedHouseUnderConstructionClosedState:
      case PropertyTypeId.detachedHouseUnderConstructionOpenState:
      case PropertyTypeId.flatUnderConstructionClosedState:
      case PropertyTypeId.flatUnderConstructionOpenState:
        return true;
      default:
        return false;
    }
  }

  @computed
  public get isFlat() {
    switch (this._estate.propertyType) {
      case PropertyTypeId.flat:
      case PropertyTypeId.flatUnderConstructionClosedState:
      case PropertyTypeId.flatUnderConstructionOpenState:
        return true;
      default:
        return false;
    }
  }

  // Packages
  @observable
  private _packages: Map<PackageId, PackageModel> = new Map<PackageId, PackageModel>();

  @computed
  public get packages() {
    return this._packages;
  }

  @action
  public setPackages(packages: Map<PackageId, PackageModel>) {
    this._packages = packages;
  }

  @observable
  private _selectedPackage: PackageId = PackageId.midi;

  @computed
  public get selectedPackage() {
    return this._selectedPackage;
  }

  @action
  public selectPackage(packageId: PackageId) {
    this.setRequireRecalculate(true);
    this._selectedPackage = packageId;
  }

  @action
  public resetSelectPackage() {
    this._selectedPackage = PackageId.midi;
  }

  @action
  public setPackageRisk(packageId: PackageId, riskId: RisksId, checked: boolean) {
    const packageData = this._packages.get(packageId);
    const riskModel = packageData?.risks.get(riskId);
    this.resetErrorRiskOnCheckBoxChange(riskId, packageId);

    if (riskModel) {
      packageData?.risks.set(riskId, { ...riskModel, available: checked });
      this.setRequireRecalculatePackage(packageId, true);
      this.setRequireRecalculate(true);
    }
  }

  @action
  public setPackageInsuranceSum(packageId: PackageId, riskId: RisksId, insuranceSum: number) {
    const packageData = this._packages.get(packageId);
    const riskModel = packageData?.risks.get(riskId);
    this.resetErrorRiskOnInsuranceSumChange(riskId);
    if (riskModel) {
      riskModel.insuranceSum = insuranceSum;
      this.setRequireRecalculatePackage(packageId, true);
      this.setRequireRecalculate(true);
    }
  }

  private resetErrorRiskOnCheckBoxChange(riskId: RisksId, packageId: PackageId) {
    if (riskId === RisksId.onlyFixedElements || riskId === RisksId.detachedHouseOrFlatWithFixedElements) {
      this.setErrorForPackageRiskInsuranceSum(packageId, RisksId.fixedElementsWithBurglaryAndTheft, false);
    }
  }

  private resetErrorRiskOnInsuranceSumChange(riskId: RisksId) {
    if (riskId === RisksId.movables || riskId === RisksId.burglaryAndTheftMovables) {
      this.setRiskInsuranceSumError(RisksId.burglaryAndTheftMovables, false);
    }
    if (riskId === RisksId.fixedElementsWithBurglaryAndTheft || riskId === RisksId.onlyFixedElements || riskId === RisksId.detachedHouseOrFlatWithFixedElements) {
      this.setRiskInsuranceSumError(RisksId.fixedElementsWithBurglaryAndTheft, false);
    }
  }

  private setRiskInsuranceSumError(riskId: RisksId, hasError: boolean) {
    this.packages.forEach((packageData: PackageModel) => {
      this.setErrorForPackageRiskInsuranceSum(packageData.id, riskId, hasError);
    });
  }

  @action
  public setErrorForPackageRisk(packageId: PackageId, riskId: RisksId, hasError: boolean) {
    const riskModel = this._packages.get(packageId)?.risks.get(riskId);

    if (riskModel) {
      riskModel.hasError = hasError;
    }
  }

  @action
  public setErrorForPackageRiskInsuranceSum(packageId: PackageId, riskId: RisksId, hasError: boolean) {
    const riskModel = this._packages.get(packageId)?.risks.get(riskId);

    if (riskModel) {
      riskModel.invalidInsuranceSum = hasError;
    }
  }

  @action
  public setRequireRecalculatePackage(packageId: PackageId, requireRecalculate: boolean) {
    const packageData = this._packages.get(packageId);

    if (packageData) {
      packageData.requireRecalculate = requireRecalculate;
    }
  }

  @action
  public setRequireRecalculatePackages() {
    this._packages.forEach((value: PackageModel) => {
      value.requireRecalculate = true;
    });
  }

  @action
  public resetPackages() {
    this.setPackages(new Package(this.estate.propertyType!).allPackages);
  }

  public noRequiredInsuranceProductsSelectedForPackage(packageId: PackageId) {
    const packageRisks = this.packages.get(packageId)?.risks;
    if (packageRisks) {
      if (!!packageRisks.get(RisksId.detachedHouseOrFlatWithFixedElements)?.available
        || !!packageRisks.get(RisksId.onlyFixedElements)?.available
        || !!packageRisks.get(RisksId.movables)?.available
      ) {
        return false;
      }
    }
    return true;
  }

  // AdditionalInsurances
  @observable
  private _additionalInsurances: AdditionalInsurancesModel = new DefaultAdditionalInsurances();

  @computed
  public get additionalInsurances() {
    return this._additionalInsurances;
  }

  @computed
  public get displaySecondOpinion() {
    const checked = this.additionalInsurances.secondOpinion.checked;
    const networkValue = this.additionalInsurances.secondOpinion.risksNetworkValue;
    return networkValue === RisksNetworkTypes.required
      || (networkValue === RisksNetworkTypes.available && checked);
  }

  @action
  public setAdditionalInsuranceFieldChecked(riskId: RisksId, checked: boolean) {
    // @ts-ignore
    const additionalInsuranceField = this._additionalInsurances[riskId];

    if (additionalInsuranceField && "checked" in additionalInsuranceField) {
      if (additionalInsuranceField.checked !== checked) {
        this.setRequireRecalculate(true);
      }
      additionalInsuranceField.checked = checked;
    }
  }

  @action
  public setInsuranceSumWithOptionalRecalculation(riskId: RisksId, insuranceSum: number, shouldRecalculate: boolean) {
    // @ts-ignore
    const additionalInsuranceField = this._additionalInsurances[riskId];

    if (additionalInsuranceField && "insuranceSum" in additionalInsuranceField) {
      additionalInsuranceField.insuranceSum = insuranceSum;
    }
    this.setRequireRecalculate(shouldRecalculate);
  }

  @action
  public setNonResidentialBuildingFlammable(flammableStructure: boolean | undefined) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.nonResidentialBuilding.flammableStructure = flammableStructure;
  }

  @action
  public setDogsCount(dogsCount: CountId) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.catAndDog.dogsCount = dogsCount;
  }

  @action
  public setCatsCount(catsCount: CountId) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.catAndDog.catsCount = catsCount;
  }

  @action
  public setOcStandard(ocGuaranteeSum: OcGuaranteeSum) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.ocStandard.ocGuaranteeSum = ocGuaranteeSum;
  }

  @action
  public setOcAdditionalPropertyAddress(fieldName: keyof SimpleAddressModel, newValue: string) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.ocAdditionalProperty.address[fieldName].value = newValue;
  }

  @observable
  private _propertyInsurance: boolean = false;

  @computed
  public get propertyInsurance() {
    return this._propertyInsurance;
  }

  @action
  public setPropertyInsurance(propertyInsurance: boolean, fromApk = false) {
    const externalApk = this.externalApk;
    const nnwWithoutProperty = this._additionalInsurances.nnwWithoutProperty.checked;
    const ocWithoutProperty = this._additionalInsurances.ocWithoutProperty.checked;
    if (fromApk) {
      if (externalApk) {
        this._propertyInsurance = true;
      } else {
        this._propertyInsurance = propertyInsurance;
      }
    } else {
      if (nnwWithoutProperty || ocWithoutProperty) {
        this._propertyInsurance = false;
      } else if (!nnwWithoutProperty && !ocWithoutProperty) {
        this._propertyInsurance = true;
      }
    }
    if (!this.propertyInsurance) {
      this._additionalInsurances.nonResidentialBuilding.checked = false;
      this._additionalInsurances.fixedEstateElements.checked = false;
      this._additionalInsurances.greenEnergySources.checked = false;
      this._additionalInsurances.catAndDog.checked = false;
      this._additionalInsurances.safeSenior.checked = false;
      this._additionalInsurances.legalProtection.checked = false;
      this._additionalInsurances.gravestone.checked = false;
    }
  }

  @computed
  public get propertyInsuranceOrOcChecked() {

    const ocSelectedFromApk = this.oc;

    const ocSelected = ocSelectedFromApk === true;

    const propertyInsuranceFromApk = this.propertyInsurance;

    const propertyInsurance = propertyInsuranceFromApk === undefined ? true : propertyInsuranceFromApk;

    return propertyInsurance || ocSelected;
  }

  @computed
  public get externalApkNoAndClientConsentNo() {
    return this.externalApkNo && this.clientConsentForApkNo;
  }

  @action
  public setLegalProtectionVariant(legalProtectionVariant: LegalProtectionVariant) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.legalProtection.variant = legalProtectionVariant;
  }

  @action
  public setNnwNumberOfClients(nnwNumberOfClients: CountId) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.nnwType.numberOfClients = nnwNumberOfClients;
    this.refreshNnwClientsSubtractionCountValue();
  }

  @action
  public setNnwNumberOfSeniors(nnwNumberOfSeniors: CountId) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.nnwSenior.nnwNumberOfSeniors = nnwNumberOfSeniors;
    this.refreshNnwClientsSubtractionCountValue();
  }

  @action
  public setNnwNumberOfHeartAttackAndStrokeInsureds(numberOfClients: CountId) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.nnwHeartAttackAndStroke.numberOfClients = numberOfClients;
    this.refreshNnwClientsSubtractionCountValue();
  }

  private refreshNnwClientsSubtractionCountValue() {
    const clientsCount = Number(this._additionalInsurances.nnwType.numberOfClients);
    const seniorsCount = Number(this._additionalInsurances.nnwSenior.nnwNumberOfSeniors);
    const heartAttackAndStrokeCount = Number(this._additionalInsurances.nnwHeartAttackAndStroke.numberOfClients);

    if (clientsCount < seniorsCount || clientsCount < heartAttackAndStrokeCount) {
      this._nnwClientsSubtractionCount.value = 1;
    } else if (clientsCount >= seniorsCount && clientsCount >= heartAttackAndStrokeCount
      && (clientsCount - seniorsCount - heartAttackAndStrokeCount) < 0) {
      this._nnwClientsSubtractionCount.value = -1;
    } else {
      this._nnwClientsSubtractionCount.value = 0;
    }
  }

  @observable
  private _nnwClientsSubtractionCount: NnwClientsSubtractionCountModel = new DefaultNnwClientsSubtractionCount();

  @computed
  public get nnwClientsSubtractionCount() {
    return this._nnwClientsSubtractionCount;
  }

  @action
  public resetAdditionalInsurances() {
    this._additionalInsurances = new DefaultAdditionalInsurances();
  }

  @action
  public resetAdditionalInsurancesWithoutNnwAndOc() {
    this._additionalInsurances.nonResidentialBuilding.checked = false;
    this._additionalInsurances.catAndDog.checked = false;
    this._additionalInsurances.fixedEstateElements.checked = false;
    this._additionalInsurances.gravestone.checked = false;
    this._additionalInsurances.greenEnergySources.checked = false;
    this._additionalInsurances.legalProtection.checked = false;
    this._additionalInsurances.safeSenior.checked = false;
  }

  @action
  public resetAdditionalInsurancesWithoutNnw() {
    this.resetAdditionalInsurancesWithoutNnwAndOc();
    this._additionalInsurances.ocWithoutProperty.checked = false;
    this._additionalInsurances.ocChildExtension.checked = false;
    this._additionalInsurances.ocAdditionalProperty.checked = false;
    this._additionalInsurances.ocRenterExtension.checked = false;
    this._additionalInsurances.ocStandard = DefaultOcStandard;
  }

  @action
  public resetAdditionalInsurancesWithoutOc() {
    this.resetAdditionalInsurancesWithoutNnwAndOc();
    this._additionalInsurances.nnwType = DefaultNnwType;
    this._additionalInsurances.nnwHeartAttackAndStroke.checked = false;
    this._additionalInsurances.nnwSenior.checked = false;
    this._additionalInsurances.nnwWithoutProperty.checked = false;

  }

  @action
  public setPropertyInsurancesOcNnw() {
    if (this.externalApkNoAndClientConsentNo) {
      this.setPropertyInsurance(true, true);
    }
    else if (this.externalApk) {
      this.setPropertyInsurance(true, true);
    }
    else {
      if (this.propertyInsuranceApk) {
        this.setPropertyInsurance(true, true);
      } else {
        this.setPropertyInsurance(false, true);
      }
    }

    const ocSelectedFromApk = this.oc;
    const ocSelected = ocSelectedFromApk === true;
    if (ocSelected) {
      this.setAdditionalInsuranceFieldChecked(RisksId.ocWithoutProperty, ocSelected);
      this.setAdditionalInsuranceFieldChecked(RisksId.ocStandard, ocSelected);
    }

    const nnwSelectedFromApk = this.nnw;
    const nnwSelected = nnwSelectedFromApk === true;
    if (nnwSelected) {
      this.setAdditionalInsuranceFieldChecked(RisksId.nnwWithoutProperty, nnwSelected);
      this.setAdditionalInsuranceFieldChecked(RisksId.nnwType, nnwSelected);
    }
  }

  @action
  public refreshCalcultionRequire() {
    const ocSelectedFromApk = this.oc;
    const ocSelected = ocSelectedFromApk === true;

    const nnwSelectedFromApk = this.nnw;
    const nnwSelected = nnwSelectedFromApk === true;

    if (ocSelected || nnwSelected) {
      this.setRequireRecalculate(true);
    } else {
      this.setRequireRecalculate(false);
    }
  }

  @action
  public setSafeSeniorCount(setSafeSeniorCount: CountId) {
    this.setRequireRecalculate(true);
    this._additionalInsurances.safeSenior.safeSeniorCount = setSafeSeniorCount;
  }

  @action
  public setAdditionalInsurancesRate(riskId: RisksId, rate: number | null) {
    // @ts-ignore
    this._additionalInsurances[riskId].rate = rate;
  }

  @action
  public resetCalculationResponse() {
    this._calculationResponse = DefaultCalculationResponse;
  }

  // AdditionalQuestions
  @observable
  private _additionalQuestions: AdditionalQuestionsModel = new DefaultAdditionalQuestions();

  @computed
  public get additionalQuestions() {
    return this._additionalQuestions;
  }

  @action
  public setAdditionalQuestions(additionalQuestions: AdditionalQuestionsModel) {
    if (this.policyStore?.isPolicyRenewal) {
      if (this._additionalQuestions?.claimlessYearsIn5Years?.value !== null
          && this._additionalQuestions?.claimlessYearsIn5Years?.value !== undefined) {
        additionalQuestions.claimlessYearsIn5Years = this._additionalQuestions.claimlessYearsIn5Years;
      }
      if (this._additionalQuestions?.numberOfDamagesInLastThreeYears?.value !== null
          && this._additionalQuestions?.numberOfDamagesInLastThreeYears?.value !== undefined) {
        additionalQuestions.numberOfDamagesInLastThreeYears = this._additionalQuestions.numberOfDamagesInLastThreeYears;
      }
    }
    this._additionalQuestions = additionalQuestions;
  }

  @action
  public resetAdditionalQuestions() {
    this._additionalQuestions = new DefaultAdditionalQuestions();
  }

  @action
  public setApkToAdditionalQuestions() {
    const businessActivity = this.businessActivity;
    const businessActivityArea = this.businessActivityInGarageOrFarmBuilding;
    const directSurvey = this._apkSurvey.isDirectSurvey();
    if (!directSurvey && !this.externalApk && this.clientConsentForApk) {
      if (businessActivity) {
        if (businessActivityArea) {
          this._additionalQuestions.businessActivityInBuilding.value = BusinessActivityId.over50percent;
        } else if (businessActivityArea === false) {
          this._additionalQuestions.businessActivityInBuilding.value = BusinessActivityId.under50percent;
        }
      } else if (businessActivity === false) {
        this._additionalQuestions.businessActivityInBuilding.value = BusinessActivityId.no;
      }

      const partOfFarm = this.farm;
      if (partOfFarm !== undefined) {
        this._additionalQuestions.partOfFarm.value = partOfFarm;
      }
    }
    else if (directSurvey) {
      if (businessActivity) {
        if (businessActivityArea) {
          this._additionalQuestions.businessActivityInBuilding.value = BusinessActivityId.over50percent;
        } else if (businessActivityArea === false) {
          this._additionalQuestions.businessActivityInBuilding.value = BusinessActivityId.under50percent;
        }
      } else if (businessActivity === false) {
        this._additionalQuestions.businessActivityInBuilding.value = BusinessActivityId.no;
      }

      const partOfFarm = this.farm;
      if (partOfFarm !== undefined) {
        this._additionalQuestions.partOfFarm.value = partOfFarm;
      }
    }
  }

  @action
  public setBusinessActivityInBuildingValue(businessActivityInBuilding: BusinessActivityId | undefined) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.businessActivityInBuilding.value = businessActivityInBuilding;
  }

  @action
  public setTypeOfActivity(val: TypeOfActivityId) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.typeOfActivity = val;
  }

  @action
  public setNumberOfDamagesInLastThreeYears(numberOfDamagesInLastThreeYears: NumberOfDamagesInLastThreeYearsId | undefined) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.numberOfDamagesInLastThreeYears.value = numberOfDamagesInLastThreeYears;
  }

  @action
  public setClaimlessYearsIn5Years(claimlessYearsIn5Years?: ClaimlessYearsId) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.claimlessYearsIn5Years.value = claimlessYearsIn5Years;
  }

  @action
  public setPolicyDuration(policyDuration: PolicyDurationId) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.policyDuration.value = policyDuration;
  }

  @action
  public setPartOfFarm(val: boolean | undefined) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.partOfFarm.value = val;
  }

  @action
  public setStartDate(startDate: Date) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.startDate = startDate;
  }

  @action
  public setPaymentMode(paymentMode: PaymentModeId | "") {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.paymentMode.value = paymentMode;
  }

  @action
  public setAdditionalProtections(additionalProtections: AdditionalProtectionsModel) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalQuestions.additionalProtections = additionalProtections;
  }

  @action
  public resetAdditionalProtections() {
    this._additionalQuestions.additionalProtections = DefaultAdditionalProtections;
  }

  @action
  public resetPropertyAdditionalQuestion() {
    this._additionalQuestions.businessActivityInBuilding = new DefaultBusinessActivityInBuilding(undefined);
    this._additionalQuestions.partOfFarm = new DefaultPartOfFarm(undefined);
  }

  // DiscountsAndAdditionalClauses
  @observable
  private _discounts = new SelectedValuesDiscount();

  @observable
  private _additionalClauses = "";

  @computed
  public get discounts() {
    return this._discounts;
  }

  @computed
  public get additionalClauses() {
    return this._additionalClauses;
  }

  @action
  public setDiscount(name: Discounts, value: string) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._discounts[name].value = value;
  }
  @action
  public setOtherClaimlessPolicyDiscount(type: OtherClaimlessPolicyDiscountTypes, value: string) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._discounts.otherClaimlessPolicyDiscount.value.value = value;
    this._discounts.otherClaimlessPolicyDiscount.value.type = type;
  }

  @action
  public setAdditionalClauses(value: string) {
    this.setRequireRecalculate(true);
    this.setRequireRecalculatePackages();
    this._additionalClauses = value;
  }

  @action
  public resetDiscountsAndAdditionalClauses() {
    this._discounts = new SelectedValuesDiscount();
    this._additionalClauses = "";
  }

  @action
  public setDiscountsAndAdditionalClauses(discounts: SelectedValuesDiscount, additionalClauses: string) {
    this._discounts = discounts;
    this._additionalClauses = additionalClauses;
  }

  // Requests
  @observable
  private _calculationRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get calculationRequestStatus(): RequestStatus {
    return this._calculationRequestStatus;
  }

  @computed
  public get allCalculationRequestStatuses(): RequestStatus[] {
    return [this.calculationRequestStatus]
      .concat(Array.from(this.packages.values()).map((packageData) => packageData.calculationRequestStatus));
  }

  @computed
  public get allRiskNetworkRequestStatuses(): RequestStatus[] {
    return Array.from(this.packages.values()).map((packageData) => packageData.riskNetworkRequestStatus);
  }

  @computed
  public get allRequestStatuses(): RequestStatus[] {
    return this.allCalculationRequestStatuses.concat(this.allRiskNetworkRequestStatuses);
  }

  @observable
  private _saveCalculationRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get saveCalculationRequestStatus(): RequestStatus {
    return this._saveCalculationRequestStatus;
  }

  @observable
  private _risksNetworkRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get risksNetworkRequestStatus(): RequestStatus {
    return this._risksNetworkRequestStatus;
  }

  // Other
  @observable
  private _requireRecalculate: boolean = false;

  @computed
  public get requireRecalculate() {
    return this._requireRecalculate;
  }

  @action
  public setRequireRecalculate(requireRecalculate: boolean) {
    this._requireRecalculate = requireRecalculate;
  }

  @observable
  private _calculationResponse: CalculationResponseModel = DefaultCalculationResponse;

  @computed
  public get calculationResponse() {
    return this._calculationResponse;
  }

  @action
  public setCalculationResponse(calculationResponse: CalculationResponseModel) {
    this._calculationResponse = calculationResponse;
  }

  @observable
  private _parentCalculationId: string | undefined;

  @computed
  public get parentCalculationId() {
    return this._parentCalculationId;
  }

  @action
  public setParentCalculationId(parentCalculationId: string | undefined) {
    this._parentCalculationId = parentCalculationId;
  }

  @action
  public resetParentCalculationId() {
    this._parentCalculationId = undefined;
  }

  @action
  public resetStore() {
    this.resetParentCalculationId();
    this.resetApk();
    this.resetEstate();
    this.resetSelectPackage();
    this.resetCalculationResponse();
    this.resetAdditionalInsurances();
    this.resetAdditionalQuestions();
    this.resetDiscountsAndAdditionalClauses();
    this.resetInitialFloodState();
    this._calculationRequestStatus = new DefaultRequestStatus();
  }
  @observable hasInitialDiscountsBeenSet: boolean = false;

  @action
  public setInitialDiscount(discountType: Discounts, value: string) {
    if (!this.hasInitialDiscountsBeenSet) {
      this.setDiscount(discountType, value);
      this.hasInitialDiscountsBeenSet = true;
    }
  }

  @observable
  private _initialFloodState: boolean = false;

  @computed
  public get initialFloodState() {
    return this._initialFloodState;
  }

  @action
  public setInitialFloodState(initialFloodState: boolean) {
    this._initialFloodState = initialFloodState;
  }

  @action
  public resetInitialFloodState() {
    this._initialFloodState = false;
  }
}
